/**
 * Form Builder Service
 * Tạo FormGroup từ schema configuration
 */

import { Injectable } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidatorFn, AsyncValidatorFn } from '@angular/forms';
import { FormSchema } from '../models/form-schema.interface';
import { FieldConfig, ValidationConfig } from '../models/field-config.interface';
import { ValidationService } from './validation.service';

@Injectable({
    providedIn: 'root'
})
export class FormBuilderService {

    constructor(
        private fb: FormBuilder,
        private validationService: ValidationService
    ) { }

    /**
     * Tạo FormGroup từ FormSchema
     */
    buildForm(schema: FormSchema, initialData?: { [key: string]: any }): FormGroup {
        const formGroup: { [key: string]: FormControl } = {};

        for (const field of schema.fields) {
            const control = this.createControl(field, initialData);
            formGroup[field.key] = control;
        }

        return this.fb.group(formGroup);
    }

    /**
     * Tạo FormControl từ FieldConfig
     */
    createControl(field: FieldConfig, initialData?: { [key: string]: any }): FormControl {
        // Lấy initial value
        const initialValue = this.getInitialValue(field, initialData);

        // Build validators
        const validators: ValidatorFn[] = field.validation
            ? this.validationService.buildValidators(field.validation)
            : [];

        // Build async validators
        const asyncValidators: AsyncValidatorFn[] = field.validation?.asyncValidators || [];

        // Tạo FormControl
        const control = new FormControl(
            { value: initialValue, disabled: field.disabled === true },
            { validators, asyncValidators, updateOn: this.getUpdateOn(field) }
        );

        return control;
    }

    /**
     * Lấy initial value cho field
     */
    private getInitialValue(field: FieldConfig, initialData?: { [key: string]: any }): any {
        // Ưu tiên: initialData > defaultValue > null
        if (initialData && initialData[field.key] !== undefined) {
            return initialData[field.key];
        }

        if (field.defaultValue !== undefined) {
            return field.defaultValue;
        }

        // Default values theo field type
        switch (field.type) {
            case 'checkbox':
                return false;
            case 'checkbox-group':
            case 'multi-select':
                return [];
            case 'number':
                return null;
            default:
                return '';
        }
    }

    /**
     * Xác định updateOn strategy
     */
    private getUpdateOn(field: FieldConfig): 'change' | 'blur' | 'submit' {
        // Nếu có async validator, update on blur để tránh gọi API quá nhiều
        if (field.validation?.asyncValidators && field.validation.asyncValidators.length > 0) {
            return 'blur';
        }
        return 'change';
    }

    /**
     * Update form values
     */
    patchFormValues(form: FormGroup, values: { [key: string]: any }): void {
        form.patchValue(values, { emitEvent: true });
    }

    /**
     * Reset form về initial values
     */
    resetForm(form: FormGroup, schema: FormSchema, initialData?: { [key: string]: any }): void {
        const resetValues: { [key: string]: any } = {};

        for (const field of schema.fields) {
            resetValues[field.key] = this.getInitialValue(field, initialData);
        }

        form.reset(resetValues);
    }

    /**
     * Enable/Disable control
     */
    setControlEnabled(form: FormGroup, fieldKey: string, enabled: boolean): void {
        const control = form.get(fieldKey);
        if (control) {
            if (enabled) {
                control.enable();
            } else {
                control.disable();
            }
        }
    }

    /**
     * Update validators cho một control
     */
    updateValidators(
        form: FormGroup,
        fieldKey: string,
        validationConfig: ValidationConfig
    ): void {
        const control = form.get(fieldKey);
        if (control) {
            const validators = this.validationService.buildValidators(validationConfig);
            control.setValidators(validators);
            control.updateValueAndValidity();
        }
    }

    /**
     * Clear validators cho một control
     */
    clearValidators(form: FormGroup, fieldKey: string): void {
        const control = form.get(fieldKey);
        if (control) {
            control.clearValidators();
            control.updateValueAndValidity();
        }
    }

    /**
     * Get form value (bao gồm cả disabled controls)
     */
    getFormValue(form: FormGroup): { [key: string]: any } {
        return form.getRawValue();
    }

    /**
     * Check if form is dirty
     */
    isFormDirty(form: FormGroup): boolean {
        return form.dirty;
    }

    /**
     * Check if form is touched
     */
    isFormTouched(form: FormGroup): boolean {
        return form.touched;
    }
}
